import {
  DIARY_CATEGORIES,
  diaryStreakOf,
  type DiaryCategory,
  type DiaryEntry,
} from "@couples/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, ImageIcon, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import DiaryCard from "../components/DiaryCard";
import DiaryEntrySheet from "../components/DiaryEntrySheet";
import ScreenHeader from "../components/ScreenHeader";
import { errorMessageOf, useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { useConfirm } from "../lib/confirm";
import { addBookmark, fetchBookmarkedEntryIds, removeBookmark } from "../lib/diary/bookmarks";
import {
  createEntry,
  deleteEntry,
  fetchEntries,
  updateEntry,
  type EntryInput,
} from "../lib/diary/entries";
import { fetchEntryMediaIds, linkEntryMedia, updateEntryMedia } from "../lib/diary/media";
import { fetchPrompts } from "../lib/diary/prompts";
import { fetchMembers } from "../lib/media/members";
import { fetchMediaByIds } from "../lib/media/notifications";
import { useMediaThumbnails } from "../lib/media/useMediaThumbnails";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import DiaryBookmarks from "./DiaryBookmarks";
import MediaDetail from "./MediaDetail";

function chipClass(active: boolean): string {
  return active
    ? "bg-[var(--color-teal)] text-white"
    : "border border-[var(--color-line)] text-[var(--color-sub)]";
}

export default function Diary() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const selfId = session?.user.id;

  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<DiaryEntry | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<DiaryCategory | null>(null);
  const [authorFilter, setAuthorFilter] = useState<string | null>(null);
  const [mediaOnly, setMediaOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  // 結び付けた写真・動画を開いた状態。その日記についているぶんの中を送れる
  const [opened, setOpened] = useState<{ ids: string[]; currentId: string } | null>(null);

  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const prompts = useQuery({ queryKey: queryKeys.diaryPrompts, queryFn: fetchPrompts });
  const entries = useQuery({ queryKey: queryKeys.diaryEntries, queryFn: fetchEntries });
  const bookmarkIds = useQuery({
    queryKey: queryKeys.diaryBookmarkIds,
    queryFn: fetchBookmarkedEntryIds,
  });
  const entryMediaIds = useQuery({
    queryKey: queryKeys.diaryEntryMedia,
    queryFn: fetchEntryMediaIds,
  });

  const allMediaIds = useMemo(
    () => [...new Set([...(entryMediaIds.data?.values() ?? [])].flat())],
    [entryMediaIds.data],
  );
  const thumbs = useMediaThumbnails(allMediaIds);

  const openedMedia = useQuery({
    queryKey: ["diary-entry-media-detail", opened?.ids],
    queryFn: () => fetchMediaByIds(opened?.ids ?? []),
    enabled: opened !== null,
  });

  const promptById = useMemo(
    () => new Map((prompts.data ?? []).map((prompt) => [prompt.id, prompt])),
    [prompts.data],
  );

  // 絞り込みチップの並び。自分を先頭にする
  const authorOptions = useMemo(() => {
    const all = [...(members.data ?? [])];
    return all.sort((a, b) => (a[0] === selfId ? -1 : b[0] === selfId ? 1 : 0));
  }, [members.data, selfId]);

  const colorOf = (authorId: string) => colorOfUser(profiles.data, authorId, selfId);

  // どちらか一方の投稿でもその日は継続扱い(→ diaryStreakOf のコメント参照)。
  // 1日だけでは通知っぽくなるので、2日以上になってから見出しに出す
  const streak = useMemo(() => diaryStreakOf(entries.data ?? []), [entries.data]);

  // 「日記」欄・「お題への回答」欄・選んだお題の本文をまとめて対象にする。
  // カテゴリ・書いた人・画像有無の絞り込みは同じ条件式の中でまとめて掛ける
  const visibleEntries = useMemo(() => {
    const all = entries.data ?? [];
    const q = query.trim().toLowerCase();
    return all.filter((entry) => {
      const prompt = entry.promptId !== null ? promptById.get(entry.promptId) : undefined;
      if (categoryFilter !== null && prompt?.category !== categoryFilter) return false;
      if (authorFilter !== null && entry.authorId !== authorFilter) return false;
      if (mediaOnly && (entryMediaIds.data?.get(entry.id) ?? []).length === 0) return false;
      if (q === "") return true;
      const haystack = [entry.body, entry.promptAnswer, prompt?.body]
        .filter((text): text is string => text !== null && text !== undefined)
        .join("\n")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    entries.data,
    promptById,
    query,
    categoryFilter,
    authorFilter,
    mediaOnly,
    entryMediaIds.data,
  ]);

  /**
   * 書いた本文を保存し、選んだ写真・動画を結び付ける。
   *
   * 本文の保存と結び付けは別のAPI呼び出し(結び付けにはエントリーIDが要る)のため、
   * 1つの useMutation にはまとめずここで順に呼ぶ。
   */
  async function handleSave(input: EntryInput, mediaIds: string[]) {
    setSaving(true);
    try {
      const entry = await createEntry(input);
      // 本文は保存できているので、結び付けだけ失敗しても日記そのものは残す
      let linkFailed = false;
      if (mediaIds.length > 0) {
        try {
          await linkEntryMedia(entry.id, mediaIds);
        } catch (e) {
          linkFailed = true;
          console.error("写真・動画の結び付けに失敗", e);
        }
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntries });
      if (selfId !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.diaryLatestOwn(selfId) });
      }
      if (mediaIds.length > 0) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntryMedia });
      }

      setComposing(false);
      if (linkFailed) {
        toast("日記を書きました。写真・動画の結び付けに失敗しました。", "warn");
      } else {
        toast("日記を書きました。");
      }
    } catch (e) {
      toast(errorMessageOf(e), "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleEditSave(input: EntryInput, mediaIds: string[]) {
    if (editing === null) return;
    setSavingEdit(true);
    try {
      await updateEntry(editing.id, input);
      await updateEntryMedia(editing.id, entryMediaIds.data?.get(editing.id) ?? [], mediaIds);
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntries });
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntryMedia });
      setEditing(null);
      toast("日記を直しました。");
    } catch (e) {
      toast(errorMessageOf(e), "error");
    } finally {
      setSavingEdit(false);
    }
  }

  const removeEntry = useMutation({
    mutationFn: deleteEntry,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntries });
      if (selfId !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.diaryLatestOwn(selfId) });
      }
      toast("日記を削除しました。");
    },
    onError: (error) => {
      toast(errorMessageOf(error), "error");
    },
  });

  const toggleBookmark = useMutation({
    mutationFn: (entry: DiaryEntry) =>
      bookmarkIds.data?.has(entry.id) === true ? removeBookmark(entry.id) : addBookmark(entry.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryBookmarks });
    },
    onError: (error) => {
      toast(errorMessageOf(error), "error");
    },
  });

  async function handleDelete(entry: DiaryEntry) {
    const ok = await confirm({
      message: "この日記を消します。",
      confirmLabel: "日記を消す",
      tone: "danger",
    });
    if (!ok) return;
    removeEntry.mutate(entry.id);
  }

  return (
    <>
      <ScreenHeader
        navKey="diary"
        menu={[
          {
            label: "しおり",
            icon: Bookmark,
            onSelect: () => {
              setShowBookmarks(true);
            },
          },
        ]}
        subtitle={
          streak >= 2 ? (
            <span className="diary-stamp self-start">
              <b className="font-bold text-[var(--color-accent)]">{streak}</b>日連続
            </span>
          ) : undefined
        }
      />

      <div className="relative flex-1">
        <div className="flex flex-col gap-3 px-4 pt-3.5 pb-24">
          <label className="relative block">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-sub)]"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder="日記を検索"
              aria-label="日記を検索"
              className="w-full rounded-lg border border-[var(--color-line)] bg-white py-2 pr-3 pl-9 text-base outline-none"
            />
          </label>

          <div className="flex flex-wrap items-center gap-1.5">
            <select
              value={categoryFilter ?? ""}
              onChange={(event) => {
                setCategoryFilter(
                  event.target.value === "" ? null : (event.target.value as DiaryCategory),
                );
              }}
              aria-label="お題のカテゴリで絞り込み"
              className="rounded-full border border-[var(--color-line)] bg-white px-3 py-1 text-xs text-[var(--color-sub)] outline-none"
            >
              <option value="">お題: すべて</option>
              {DIARY_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            {authorOptions.map(([id, name]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setAuthorFilter((current) => (current === id ? null : id));
                }}
                aria-pressed={authorFilter === id}
                className={`shrink-0 rounded-full px-3 py-1 text-xs ${chipClass(authorFilter === id)}`}
              >
                {name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setMediaOnly((current) => !current);
              }}
              aria-pressed={mediaOnly}
              className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs ${chipClass(
                mediaOnly,
              )}`}
            >
              <ImageIcon className="size-3" aria-hidden />
              画像あり
            </button>

            {/* PC の主操作。スマホ・タブレットは右下の丸ボタンが受け持つ(一覧が伸びても
                画面に留まるよう fixed にしている。→ 下の丸ボタン側のコメントも参照) */}
            <button
              type="button"
              onClick={() => {
                setComposing(true);
              }}
              className="ml-auto hidden shrink-0 items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-85 xl:flex"
            >
              <Plus className="size-4" aria-hidden />
              日記を書く
            </button>
          </div>

          {entries.isError && (
            <p role="alert" className="text-sm text-[var(--color-plum)]">
              日記を取得できませんでした。
            </p>
          )}
          {entries.isSuccess && entries.data.length === 0 && (
            <p className="py-10 text-center text-sm text-[var(--color-sub)]">
              まだ日記がありません。右下のボタンから書いてみましょう。
            </p>
          )}
          {entries.isSuccess && entries.data.length > 0 && visibleEntries.length === 0 && (
            <p className="py-10 text-center text-sm text-[var(--color-sub)]">
              検索条件に合う日記がありません。
            </p>
          )}
          {visibleEntries.map((entry) => (
            <DiaryCard
              key={entry.id}
              entry={entry}
              authorName={members.data?.get(entry.authorId) ?? "?"}
              authorColor={colorOf(entry.authorId)}
              prompt={entry.promptId !== null ? promptById.get(entry.promptId) : undefined}
              isBookmarked={bookmarkIds.data?.has(entry.id) === true}
              onToggleBookmark={() => void toggleBookmark.mutate(entry)}
              onEdit={
                entry.authorId === selfId
                  ? () => {
                      setEditing(entry);
                    }
                  : undefined
              }
              onDelete={entry.authorId === selfId ? () => void handleDelete(entry) : undefined}
              mediaIds={entryMediaIds.data?.get(entry.id) ?? []}
              thumbUrlOf={thumbs.urlOf}
              onOpenMedia={(mediaId) => {
                setOpened({ ids: entryMediaIds.data?.get(entry.id) ?? [], currentId: mediaId });
              }}
            />
          ))}
        </div>

        {/* 一覧が伸びるほど下に沈む absolute ではなく、ビューポート基準の fixed で
            常にスクロール位置に関わらず親指の届く位置に留める。PC は上の主操作ボタンが受け持つ */}
        <div className="pointer-events-none fixed inset-x-0 z-40 mx-auto flex max-w-[var(--content-w)] justify-end px-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] xl:hidden">
          <button
            type="button"
            onClick={() => {
              setComposing(true);
            }}
            aria-label="日記を書く"
            className="pointer-events-auto flex size-[46px] items-center justify-center rounded-full bg-[var(--color-teal)] shadow-lg"
          >
            <Plus className="size-5 text-white" aria-hidden />
          </button>
        </div>
      </div>

      {composing && (
        <DiaryEntrySheet
          prompts={prompts.data ?? []}
          busy={saving}
          onSave={(input, mediaIds) => void handleSave(input, mediaIds)}
          onClose={() => {
            setComposing(false);
          }}
        />
      )}

      {editing !== null && (
        <DiaryEntrySheet
          prompts={prompts.data ?? []}
          editingEntry={editing}
          initialMediaIds={entryMediaIds.data?.get(editing.id) ?? []}
          busy={savingEdit}
          onSave={(input, mediaIds) => void handleEditSave(input, mediaIds)}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      {showBookmarks && (
        <DiaryBookmarks
          onClose={() => {
            setShowBookmarks(false);
          }}
        />
      )}

      {opened !== null && openedMedia.data !== undefined && openedMedia.data.length > 0 && (
        <MediaDetail
          items={openedMedia.data}
          currentId={opened.currentId}
          thumbnailUrls={thumbs.urls}
          onChange={(id) => {
            setOpened((current) => (current === null ? null : { ...current, currentId: id }));
          }}
          onClose={() => {
            setOpened(null);
          }}
        />
      )}
    </>
  );
}
